import { useEffect, useState } from 'react';
import { Rating } from '../../components/rating/rating';
import { buildParams, concatPath, overlayKey } from '../../helpers/path';
import { useIndexedDB } from '../../hooks/use-indexeddb/use-indexed-db';
import { useLang } from '../../hooks/use-lang';
import { useQueryParams } from '../../hooks/use-query-params';
import { ManagerPath, ReviewsPath, RootPath, StaffPath } from '../../routes/manager/manager-enums';
import { CheckInType } from '../../store/api/check-in-check-out-api';
import { ReviewType } from '../../store/api/reviews-api';
import { TaskType } from '../../store/api/tasks-api';
import { useAlertStore } from '../../store/use-alert-store';
import { useCheckInCheckOutStore } from '../../store/use-check-in-check-out-store';
import { useReviewsStore } from '../../store/use-reviews-store';
import { useTaskStore } from '../../store/use-task-store';
import { sortByCost } from '../table/helpers';
import { decodeHTML, FirebaseNotificationType } from './notification-helpers';
import s from './notifications.module.css';
import { useFirebase } from './use-firebase/use-firebase';

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<FirebaseNotificationType[]>([]);
  const params = useQueryParams().getQueryParams();
  const overlayId = params[overlayKey];
  const { alerts, add: addAlert } = useAlertStore();
  const { add, getAll, removeById } = useIndexedDB('notifications');
  const { handleUpdateReviews } = useReviewsStore();
  const { handleUpdateTasks } = useTaskStore();
  const { handleUpdateCheckInCheckOut } = useCheckInCheckOutStore();
  const { t } = useLang();
  const [isLoading, setIsLoading] = useState(true);

  const handleGetNotifications = async () => {
    setIsLoading(true);
    const resp = (await getAll()) as FirebaseNotificationType[];
    resp.sort((a, b) => sortByCost(a.createdAt, b.createdAt));
    setNotifications(resp);
    setIsLoading(false);
  };

  const handleRemoveNotification = async (id: string) => {
    removeById(id).then(handleGetNotifications);
  };

  useEffect(() => {
    handleGetNotifications();
  }, [alerts?.length, overlayId]);

  const handleAddAlert = async (item: FirebaseNotificationType, src: unknown) => {
    const { id, type } = item;
    await add(id, item);

    if (type === 'review') {
      handleAddReview(item, src as ReviewType);
    }
    if (['task', 'order'].includes(type)) {
      handleAddTask(item, src as TaskType);
    }

    if (['delay', 'possible_delay', 'serious_delay'].includes(type)) {
      handleAddCheckInOut(item, src as CheckInType);
    }
  };

  const handleAddReview = async (item: FirebaseNotificationType, src: ReviewType) => {
    const reviewType = src.task_type || ReviewsPath.Branch;
    src.content = decodeHTML(src.content);

    addAlert({
      label: t(item.title),
      variant: 'info',
      endless: true,
      link:
        concatPath([RootPath.Manager, ManagerPath.Reviews, reviewType.toLowerCase()]) +
        buildParams({ [overlayKey]: item.id }),
      children: (
        <div className={s.review}>
          <Rating rate={src.rating} />
          <p dangerouslySetInnerHTML={{ __html: decodeHTML(item.body) }} />
        </div>
      ),
    });
    handleUpdateReviews(src);
  };

  const handleAddTask = async (item: FirebaseNotificationType, src: TaskType) => {
    src.comment = decodeHTML(src.comment);
    src.client_comment = decodeHTML(src.client_comment);

    addAlert({
      label: t(item.title),
      variant: 'info',
      endless: true,
      link:
        concatPath([RootPath.Manager, ManagerPath.Tasks, src.type.toLowerCase()]) +
        buildParams({ [overlayKey]: item.id }),
      children: (
        <div className={s.review}>
          <p dangerouslySetInnerHTML={{ __html: decodeHTML(item.body) }} />
        </div>
      ),
    });
    handleUpdateTasks(src);
  };

  const handleAddCheckInOut = async (item: FirebaseNotificationType, src?: CheckInType) => {
    addAlert({
      label: t(item.title),
      variant: 'info',
      endless: true,
      link: src
        ? concatPath([RootPath.Manager, ManagerPath.Staff, StaffPath.CheckInCheckOut]) +
          buildParams({ [overlayKey]: item.id })
        : undefined,
      children: <p dangerouslySetInnerHTML={{ __html: decodeHTML(item.body) }} />,
    });
    if (src) {
      handleUpdateCheckInCheckOut(src);
    }
  };

  useFirebase({
    handleAddIndexedDb: handleAddAlert,
    handleGetNotifications,
  });

  return {
    handleRemoveNotification,
    t,
    notifications,
    isLoading,
  };
};
